import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, User } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import questionService from '../../services/questionService';
import toast from 'react-hot-toast';

const OwnerReplyModal = ({ question, isOpen, onClose, onSuccess }) => {
  const [answer, setAnswer] = useState('');
  const [charCount, setCharCount] = useState(0);
  const maxChars = 2000;
  const minChars = 10;

  // Pre-fill with existing answer if editing
  useEffect(() => {
    if (question.answer) {
      setAnswer(question.answer.answer);
      setCharCount(question.answer.answer.length);
    }
  }, [question.answer]);

  const answerMutation = useMutation({
    mutationFn: (answerText) => questionService.answerQuestion(question.id, answerText),
    onSuccess: (data) => {
      toast.success(question.answer ? 'Answer updated successfully!' : 'Question answered successfully!');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to submit answer');
    }
  });

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= maxChars) {
      setAnswer(value);
      setCharCount(value.length);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (answer.trim().length >= minChars && answer.trim().length <= maxChars) {
      answerMutation.mutate(answer.trim());
    }
  };

  const handleClose = () => {
    if (!answerMutation.isLoading) {
      onClose();
    }
  };

  const isValid = answer.trim().length >= minChars && answer.trim().length <= maxChars;

  const getUserDisplayName = (user) => {
    return user.full_name || user.pi_username || 'Anonymous';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
        >
          <Card className="shadow-2xl border-0">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center justify-between">
                <span>{question.answer ? 'Edit Answer' : 'Answer Question'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  disabled={answerMutation.isLoading}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Original Question */}
              <div className="p-4 bg-neutral-50 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-medium text-neutral-700">Original Question:</span>
                </div>
                <p className="text-neutral-900 font-medium mb-3">
                  {question.question}
                </p>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  {question.user.profile_image ? (
                    <img 
                      src={question.user.profile_image}
                      alt={getUserDisplayName(question.user)}
                      className="w-4 h-4 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-4 h-4 bg-gradient-to-br from-primary-teal to-primary-teal/80 rounded-full flex items-center justify-center">
                      <User className="w-2 h-2 text-white" />
                    </div>
                  )}
                  <span>Asked by {getUserDisplayName(question.user)}</span>
                </div>
              </div>

              {/* Answer Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="answer" className="block text-sm font-medium text-neutral-900 mb-2">
                    Your Answer
                  </label>
                  <textarea
                    id="answer"
                    value={answer}
                    onChange={handleInputChange}
                    placeholder="Provide a detailed and helpful answer to this question..."
                    className="w-full px-3 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-teal focus:border-transparent resize-none transition-colors"
                    rows={8}
                    disabled={answerMutation.isLoading}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xs text-neutral-500">
                      {charCount < minChars && (
                        <span className="text-secondary-red">
                          Minimum {minChars} characters required
                        </span>
                      )}
                      {charCount >= minChars && charCount <= maxChars && (
                        <span className="text-primary-teal">
                          Good length for a comprehensive answer
                        </span>
                      )}
                    </div>
                    <div className={`text-xs ${
                      charCount > maxChars * 0.9 ? 'text-secondary-red' : 'text-neutral-500'
                    }`}>
                      {charCount}/{maxChars}
                    </div>
                  </div>
                </div>

                {/* Tips */}
                <div className="p-3 bg-primary-teal/5 rounded-lg border border-primary-teal/20">
                  <h4 className="text-sm font-medium text-primary-teal mb-2">💡 Tips for a great answer:</h4>
                  <ul className="text-xs text-neutral-700 space-y-1">
                    <li>• Be specific and detailed in your response</li>
                    <li>• Address all parts of the question</li>
                    <li>• Provide examples or additional context when helpful</li>
                    <li>• Keep a professional and friendly tone</li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-4 border-t border-neutral-200">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={answerMutation.isLoading}
                    className="w-full sm:w-auto order-2 sm:order-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={!isValid || answerMutation.isLoading}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center order-1 sm:order-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>
                      {answerMutation.isLoading 
                        ? 'Submitting...' 
                        : question.answer 
                          ? 'Update Answer' 
                          : 'Submit Answer'
                      }
                    </span>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OwnerReplyModal;
